// Composite-path target-id builder.
//
// The synthesized briefing payload doesn't carry stable per-item IDs, so
// the SPA encodes a deterministic path-key into each row's `target.id`
// when it submits feedback. The slice-3 absorber treats `comment` and
// `flag_next_week` events as OperatorNotes (no per-item lookup), so this
// is a SPA-defined contract with no agent-side dependency.
//
// Examples (legacy + v2):
//   thread:electrical-recertification/section:completed/index:0
//   unthreaded/section:blockers/index:2
//   thread:electrical-recertification          (thread-level: rename, merge, retag, close)
//   thread:electrical/work_item:block-b        (work-item level: close, reopen, comment)
//   thread:electrical/work_item:block-b/section:blockers/index:0
//   unthreaded/work_item:vendor-onboarding/section:action_items/index:1

export type Section =
  | "completed"
  | "milestones"
  | "in_progress"
  | "decisions"
  | "blockers"
  | "action_items"
  | "open_questions"
  | "leadership_attention";

// Derive an Action's slug from its free text — the SPA mirror of the agent's
// `agent/action_items.py::_derive_ai_slug` (added v0.4.9.2, `483688c9`): slugify,
// then cap at 60 chars on a word boundary. MUST match the agent byte-for-byte: the
// SPA mints/echoes an Action slug from text (action_add / place_as_action, and the
// place_from_unfiled optimistic echo) while the agent stores the *capped* form, so an
// uncapped slug dead-letters any follow-up correction on that Action until the next
// cron re-renders it (the 2026-10-06 `north-tower` incident — SLUG-CAP-MISMATCH;
// docs/plans/slug-cap-mismatch-premise-gate-2026-10-06.md). Threads/Items use the
// uncapped `config.slugify` on BOTH sides, so they are deliberately NOT capped here —
// this is Action-only.
export function actionItemSlug(text: string): string {
  const s = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (s.length <= 60) return s;
  const cut = s.slice(0, 60);
  const i = cut.lastIndexOf("-");
  return i >= 0 ? cut.slice(0, i) : cut;
}

export function threadKey(slug: string): string {
  return `thread:${slug}`;
}

export function threadItemKey(
  slug: string,
  section: Section,
  index: number,
): string {
  return `thread:${slug}/section:${section}/index:${index}`;
}

export function unthreadedItemKey(section: Section, index: number): string {
  return `unthreaded/section:${section}/index:${index}`;
}

export function needsReviewItemKey(itemId: string): string {
  // Needs-review entries already carry the underlying item_id from the
  // ingest store; preserve it so a `review_clarification` event can be
  // matched back to the canonical record.
  return itemId;
}

// --- Slice 7: work-item-scoped keys -----------------------------------------

export function workItemKey(threadSlug: string, workItemSlug: string): string {
  return `thread:${threadSlug}/work_item:${workItemSlug}`;
}

export function unthreadedWorkItemKey(workItemSlug: string): string {
  return `unthreaded/work_item:${workItemSlug}`;
}

export function workItemSectionItemKey(
  threadSlug: string,
  workItemSlug: string,
  section: Section,
  index: number,
): string {
  return `thread:${threadSlug}/work_item:${workItemSlug}/section:${section}/index:${index}`;
}

export function unthreadedWorkItemSectionItemKey(
  workItemSlug: string,
  section: Section,
  index: number,
): string {
  return `unthreaded/work_item:${workItemSlug}/section:${section}/index:${index}`;
}

// --- v4 (Task #9) -----------------------------------------------------------
//
// Mirror of the agent's `_parse_action_item_target` (feedback_absorb.py)
// and `_parse_work_item_target` contracts. Path notation only ever sees
// kebab-case slugs, so URL encoding isn't needed.

/** Action-item key — `thread:<t>/work_item:<wi>/action_item:<ai>`. */
export function actionItemKey(
  threadSlug: string,
  workItemSlug: string,
  actionItemSlug: string,
): string {
  return `thread:${threadSlug}/work_item:${workItemSlug}/action_item:${actionItemSlug}`;
}

/** Unfiled-row key — `unfiled:<id>`. The id is the SPA-stable id the
 *  v4 renderer emits per Unfiled row; the agent treats it as opaque. */
export function unfiledKey(id: string): string {
  return `unfiled:${id}`;
}

// --- #38: sub-category grouping --------------------------------------------
//
// Sub-categories are an unnumbered, recipient-created grouping ATTRIBUTE
// inside a Category (the overlay model — they live in state, never in the
// synth payload). Their target id mirrors work_item:
//   thread:<thread_slug>/subcategory:<subcategory_slug>
// used by subcategory_rename / _close / _reopen. subcategory_add targets the
// parent `thread:<t>`; item_move_to_subcategory targets the
// `thread:<t>/work_item:<wi>`. Distinct ids keep the SPA's per-target
// feedback-chip grouping clean. Mirrors the agent absorber's
// `_parse_subcategory_target` (feedback_absorb.py).

/** Sub-category key — `thread:<t>/subcategory:<sc>`. */
export function subcategoryKey(
  threadSlug: string,
  subcategorySlug: string,
): string {
  return `thread:${threadSlug}/subcategory:${subcategorySlug}`;
}

/** Informational-row key — `informational:<id>` (A³/A², 2026-06-03). The id
 *  is the stable `info-<hash>` the relocate guard emits per bucket entry;
 *  `_parse_informational_target` decodes it back to the info_id. */
export function informationalKey(id: string): string {
  return `informational:${id}`;
}
